import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import InfoIcon from "./InfoIcon";
import { AiOutlineClose } from "react-icons/ai";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { Range, getTrackBackground } from "react-range";
import "../styles/LayerItem.css";
import debounce from "lodash.debounce";
import OpacitySlider from "./OpacitySlider";
import Legend from "./Legend";
import layersConfig from "../config/layersConfig";
import YearRangeSlider from "./YearRangeSlider";
import DatePillPopup from "./DatePillPopup";
import { ImSpinner2 } from "react-icons/im";

const STEP = 1;
const MIN_YEAR = 2001;
const MAX_YEAR = 2025;
const FOREST_LOSS_MIN_YEAR = 2001;
const FOREST_LOSS_MAX_YEAR = 2023;
const BIOMASS_YEARS = [2010, 2017, 2018, 2019, 2020];

// Create dates at UTC noon
const currentDate = new Date();
currentDate.setUTCHours(12, 0, 0, 0);

const threeYearsAgo = new Date(currentDate);
threeYearsAgo.setFullYear(currentDate.getFullYear() - 3);
threeYearsAgo.setUTCHours(12, 0, 0, 0);

// Format dates consistently
function formatYMD(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const EARLIEST_DEF_DATE = formatYMD(threeYearsAgo);
const LATEST_DEF_DATE = formatYMD(currentDate);

// Reference date (set to UTC noon)
const REF_DATE = new Date(EARLIEST_DEF_DATE + 'T12:00:00Z');

function daysFromRef(dateStr) {
  const msPerDay = 1000 * 60 * 60 * 24;
  const date = new Date(dateStr + 'T12:00:00Z');
  const diff = date.getTime() - REF_DATE.getTime();
  return Math.round(diff / msPerDay);
}

function dateFromDays(daysOffset) {
  const msPerDay = 1000 * 60 * 60 * 24;
  const date = new Date(REF_DATE.getTime() + daysOffset * msPerDay);
  return formatYMD(date);
}

const LayerItem = ({
  layerId,
  layerName,
  layerDescription = "",
  isVisible,
  onToggleVisibility,
  onOpenInfo,
  opacity,
  onOpacityChange,
  onRemove,
  // forest-loss
  isForestLossRange = false,
  yearRange = { start: 2001, end: 2023 },
  updateYearRange = null,
  updateYearSelection = null,
  // deforestation
  deforestationRange,
  updateDeforestationRange,
  loading = false,
  isDragging,
  dragHandleProps,
  // NEW: biomass year prop (controlled by parent)
  selectedYear,
}) => {
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  const validatedOpacity = typeof opacity === "number" ? opacity : 100;

  const layerConfig = layersConfig.find((l) => l.id === layerId) || {};
  const {
    colors = [],
    labels = [],
    Description = [],
    title: legendTitle,
    type: legendType = "gradient",
    min = 0,
    max = 25,
    numLabels = 5,
  } = layerConfig.legend || {};

  // Forest loss range handling
  const [yearValues, setYearValues] = useState([yearRange.start, yearRange.end]);
  const debouncedUpdateYearRange = useRef(
    debounce((newRange) => {
      updateYearRange?.(newRange);
    }, 300)
  ).current;

  useEffect(() => {
    if (isForestLossRange && updateYearRange) {
      setYearValues([yearRange.start, yearRange.end]);
    }
  }, [isForestLossRange, yearRange, updateYearRange]);

  const handleForestLossSliderChange = useCallback(
    (values) => {
      setYearValues(values);
      if (updateYearRange) {
        const [start, end] = values;
        debouncedUpdateYearRange({ start, end });
      }
    },
    [debouncedUpdateYearRange, updateYearRange]
  );

  // Deforestation dates handling
  const [localStartDate, setLocalStartDate] = useState(() => {
    const date = deforestationRange?.startDate
      ? new Date(deforestationRange.startDate + 'T12:00:00Z')
      : new Date(EARLIEST_DEF_DATE + 'T12:00:00Z');
    return date;
  });

  const [localEndDate, setLocalEndDate] = useState(() => {
    const date = deforestationRange?.endDate
      ? new Date(deforestationRange.endDate + 'T12:00:00Z')
      : new Date(LATEST_DEF_DATE + 'T12:00:00Z');
    return date;
  });

  const [defSliderValues, setDefSliderValues] = useState(() => [
    daysFromRef(formatYMD(localStartDate)),
    daysFromRef(formatYMD(localEndDate))
  ]);

  // Handle deforestation range updates from parent
  useEffect(() => {
    if (layerId === "deforestation-alerts-malaysia" && deforestationRange) {
      const newStart = new Date(deforestationRange.startDate + 'T12:00:00Z');
      const newEnd = new Date(deforestationRange.endDate + 'T12:00:00Z');

      const startStr = formatYMD(localStartDate);
      const endStr = formatYMD(localEndDate);

      if (deforestationRange.startDate !== startStr || deforestationRange.endDate !== endStr) {
        setLocalStartDate(newStart);
        setLocalEndDate(newEnd);
        setDefSliderValues([
          daysFromRef(deforestationRange.startDate),
          daysFromRef(deforestationRange.endDate)
        ]);
      }
    }
  }, [deforestationRange, layerId]);

  // Handle slider changes
  const onDeforestationSliderChange = useCallback((values) => {
    setDefSliderValues(values);

    const newStartStr = dateFromDays(values[0]);
    const newEndStr = dateFromDays(values[1]);

    const newStart = new Date(newStartStr + 'T12:00:00Z');
    const newEnd = new Date(newEndStr + 'T12:00:00Z');

    setLocalStartDate(newStart);
    setLocalEndDate(newEnd);

    if (updateDeforestationRange) {
      updateDeforestationRange({
        startDate: newStartStr,
        endDate: newEndStr
      });
    }
  }, [updateDeforestationRange]);

  // Handle date picker changes
  const handleStartDateChange = useCallback(
    (newDate) => {
      if (newDate <= localEndDate) {
        newDate.setUTCHours(12, 0, 0, 0);
        setLocalStartDate(newDate);

        const dateStr = formatYMD(newDate);
        const newValues = [
          daysFromRef(dateStr),
          daysFromRef(formatYMD(localEndDate))
        ];
        setDefSliderValues(newValues);

        if (updateDeforestationRange) {
          updateDeforestationRange({
            startDate: dateStr,
            endDate: formatYMD(localEndDate)
          });
        }
      }
    },
    [localEndDate, updateDeforestationRange]
  );

  const handleEndDateChange = useCallback(
    (newDate) => {
      if (newDate >= localStartDate) {
        newDate.setUTCHours(12, 0, 0, 0);
        setLocalEndDate(newDate);

        const dateStr = formatYMD(newDate);
        const newValues = [
          daysFromRef(formatYMD(localStartDate)),
          daysFromRef(dateStr)
        ];
        setDefSliderValues(newValues);

        if (updateDeforestationRange) {
          updateDeforestationRange({
            startDate: formatYMD(localStartDate),
            endDate: dateStr
          });
        }
      }
    },
    [localStartDate, updateDeforestationRange]
  );

  // Biomass handling using the new selectedYear prop
  const [biomassIndex, setBiomassIndex] = useState(() => {
    const idx = BIOMASS_YEARS.indexOf(selectedYear || 2020);
    return idx >= 0 ? idx : BIOMASS_YEARS.length - 1;
  });

  useEffect(() => {
    const idx = BIOMASS_YEARS.indexOf(selectedYear || 2020);
    if (idx >= 0) {
      setBiomassIndex(idx);
    }
  }, [selectedYear]);

  const handleBiomassSliderChange = useCallback((values) => {
    setBiomassIndex(values[0]);
  }, []);

  const handleBiomassSliderFinalChange = useCallback(
    (values) => {
      const actualYear = BIOMASS_YEARS[values[0]];
      if (updateYearSelection) {
        updateYearSelection(actualYear);
      }
    },
    [updateYearSelection]
  );

  // Cleanup
  useEffect(() => {
    return () => {
      debouncedUpdateYearRange.cancel();
    };
  }, [debouncedUpdateYearRange]);

  return (
    <div
      className={`layer-item ${isVisible ? "active" : ""} ${
        isDragging ? "dragging" : ""
      } ${
        layerId === "deforestation-alerts-malaysia" ? "deforestation-layer" : ""
      }`}
    >
      <div className="layer-content">
        {/* Drag handle */}
        <div className="drag-handles-container" {...dragHandleProps}>
          <div className="drag-handles">
            <div className="drag-line"></div>
            <div className="drag-line"></div>
          </div>
        </div>

        {/* Layer header */}
        <div className="layer-header">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={() => onToggleVisibility(layerId)}
              aria-label={`Toggle ${layerName}`}
            />
            <span className="slider"></span>
          </label>

          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => setLegendCollapsed(!legendCollapsed)}
          >
            <span className="layer-name">{layerName}</span>
            {loading && (
              <ImSpinner2
                className="layer-loading-spinner"
                title="Loading..."
                style={{ marginLeft: 8 }}
              />
            )}
            {legendCollapsed ? (
              <BsChevronDown style={{ marginLeft: 6 }} />
            ) : (
              <BsChevronUp style={{ marginLeft: 6 }} />
            )}
          </div>

          <div className="header-icons">
            <InfoIcon
              onClick={() => onOpenInfo(layerConfig)}
              ariaLabel="Layer Information"
              title="Layer Information"
            />
            <AiOutlineClose
              className="remove-icon"
              title="Remove Layer"
              onClick={() => onRemove(layerId)}
            />
          </div>
        </div>

        {/* Advanced Settings */}
        {isVisible && !legendCollapsed && (
          <div className="layer-advanced-settings">
            {/* Legend */}
            <div className="setting-item legend">
              <Legend
                colors={colors.length ? colors : ["#FFC0C0", "#FF0000"]}
                labels={labels}
                description={Description}
                title={legendTitle}
                type={legendType}
                min={min}
                max={max}
                numLabels={numLabels}
                shape={layerConfig?.legend?.shape || "circle"}
              />
            </div>

            {/* Forest-loss range slider */}
            {isForestLossRange && (
              <div className="setting-item year-range">
                <div
                  className="year-range-slider-container"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <span>Filter:&nbsp;{FOREST_LOSS_MIN_YEAR}</span>
                  <Range
                    values={yearValues}
                    step={STEP}
                    min={FOREST_LOSS_MIN_YEAR}
                    max={FOREST_LOSS_MAX_YEAR}
                    onChange={handleForestLossSliderChange}
                    renderTrack={({ props, children }) => (
                      <div
                        onMouseDown={props.onMouseDown}
                        onTouchStart={props.onTouchStart}
                        style={{
                          ...props.style,
                          height: "2px",
                          width: "100%",
                          margin: "0 10px",
                          background: getTrackBackground({
                            values: yearValues,
                            colors: ["#ccc", "#808080", "#ccc"],
                            min: FOREST_LOSS_MIN_YEAR,
                            max: FOREST_LOSS_MAX_YEAR,
                          }),
                        }}
                        ref={props.ref}
                      >
                        {children}
                      </div>
                    )}
                    renderThumb={({ index, props }) => (
                      <div
                        {...props}
                        className="v-slider__thumb-label pin-thumb"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="pin">
                          <div className="pin-value">{yearValues[index]}</div>
                        </div>
                      </div>
                    )}
                  />
                  <span>{FOREST_LOSS_MAX_YEAR}</span>
                </div>
              </div>
            )}

            {/* Deforestation date range */}
            {layerId === "deforestation-alerts-malaysia" && (
              <div className="setting-item deforestation-date-picker">
                <div className="slider-container date-pickers-container">
                  <span className="filter-label">From:</span>
                  <DatePillPopup
                    date={localStartDate}
                    onChange={handleStartDateChange}
                    minDate={new Date(EARLIEST_DEF_DATE + 'T12:00:00Z')}
                    maxDate={new Date(LATEST_DEF_DATE + 'T12:00:00Z')}
                  />

                  <span className="filter-label">To:</span>
                  <DatePillPopup
                    date={localEndDate}
                    onChange={handleEndDateChange}
                    minDate={localStartDate}
                    maxDate={new Date(LATEST_DEF_DATE + 'T12:00:00Z')}
                  />
                </div>

                <div className="slider-container deforestation-slider-wrapper">
                  <span className="filter-label">Date Range:</span>
                  <div className="slider-range">
                    <YearRangeSlider
                      values={defSliderValues}
                      step={1}
                      min={daysFromRef(EARLIEST_DEF_DATE)}
                      max={daysFromRef(LATEST_DEF_DATE)}
                      onChange={onDeforestationSliderChange}
                      renderThumbContent={(index) =>
                        dateFromDays(defSliderValues[index])
                      }
                      showPin={false}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Biomass slider */}
            {layerId === "biomass-layer-id" && updateYearSelection && (
              <div className="setting-item biomass-year">
                <div style={{ position: "relative", width: "90%" }}>
                  <Range
                    values={[biomassIndex]}
                    step={1}
                    min={0}
                    max={BIOMASS_YEARS.length - 1}
                    onChange={handleBiomassSliderChange}
                    onFinalChange={handleBiomassSliderFinalChange}
                    renderTrack={({ props, children }) => (
                      <div
                        onMouseDown={props.onMouseDown}
                        onTouchStart={props.onTouchStart}
                        style={{
                          ...props.style,
                          height: "2px",
                          width: "100%",
                          margin: "8px 0",
                          background: getTrackBackground({
                            values: [biomassIndex],
                            colors: ["#ccc", "#808080", "#ccc"],
                            min: 0,
                            max: BIOMASS_YEARS.length - 1,
                          }),
                        }}
                        ref={props.ref}
                      >
                        {children}
                      </div>
                    )}
                    renderThumb={({ index, props }) => (
                      <div
                        {...props}
                        className="v-slider__thumb-label pin-thumb"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="pin">
                          <div className="pin-value">
                            {BIOMASS_YEARS[biomassIndex]}
                          </div>
                        </div>
                      </div>
                    )}
                  />
                  <div className="biomass-slider-dots">
                    {BIOMASS_YEARS.map((year, i) => {
                      const fraction = i / (BIOMASS_YEARS.length - 1);
                      const isSelected = i === biomassIndex;
                      return (
                        <div
                          key={year}
                          className={`biomass-slider-dot ${
                            isSelected ? "selected" : ""
                          }`}
                          style={{ left: `${fraction * 100}%` }}
                        />
                      );
                    })}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  {BIOMASS_YEARS.map((year) => (
                    <span key={year} style={{ fontSize: "0.75rem" }}>
                      {year}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Opacity slider */}
            <div className="setting-item opacity">
              <OpacitySlider
                layerId={layerId}
                initialOpacity={validatedOpacity}
                onOpacityChange={onOpacityChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

LayerItem.propTypes = {
  layerId: PropTypes.string.isRequired,
  layerName: PropTypes.string.isRequired,
  layerDescription: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  onToggleVisibility: PropTypes.func.isRequired,
  onOpenInfo: PropTypes.func.isRequired,
  opacity: PropTypes.number.isRequired,
  onOpacityChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isForestLossRange: PropTypes.bool,
  yearRange: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  updateYearRange: PropTypes.func,
  updateYearSelection: PropTypes.func,
  deforestationRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  updateDeforestationRange: PropTypes.func,
  loading: PropTypes.bool,
  isDragging: PropTypes.bool,
  dragHandleProps: PropTypes.object,
  // NEW: biomass slider props
  selectedYear: PropTypes.number,
};

export default React.memo(LayerItem);
